declare const fbq: (e: string, n: string) => void | undefined;
declare const gtag: (e: string, t: string, p: any) => void | undefined;
declare const spdt: (e: string, p: any) => void | undefined;

export type EventType =
  | "login"
  | "logout"
  | "signup"
  | "action"
  | "view"
  | "authorized"
  | "user-created"
  | "email-verified";

type Metadata = {
  eventType: EventType;
  url: string;
  qsps: string;
  referrer: string;
  title: string;
  timestamp: number;
  browser: string;
  device: string;
  version: string;
};

type EventData = {
  action?: string | null;
  eventId?: number | string | null;
  rodEventId?: number | string | null;
  teamId?: number | string | null;
  email?: string | null;
  code?: string | null;
};

export const getBrowserAndDevice = () => {
  const userAgent = navigator.userAgent;
  let browser = "Unknown";
  let device = "Unknown";

  // Browser detection
  if (userAgent.indexOf("Chrome") > -1) {
    browser = "Chrome";
  } else if (userAgent.indexOf("Firefox") > -1) {
    browser = "Firefox";
  } else if (userAgent.indexOf("Safari") > -1) {
    browser = "Safari";
  } else if (userAgent.indexOf("Edge") > -1) {
    browser = "Edge";
  } else if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) {
    browser = "Opera";
  } else if (
    userAgent.indexOf("MSIE") > -1 ||
    userAgent.indexOf("Trident/") > -1
  ) {
    browser = "Internet Explorer";
  }

  // Device detection
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent
    )
  ) {
    device = "Mobile";
  } else if (/Tablet|iPad/i.test(userAgent)) {
    device = "Tablet";
  } else {
    device = "Desktop";
  }

  return { browser, device };
};

export const getLocation = (
  successFunction: (pos: GeolocationPosition) => void
) => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(successFunction, () => {}, {
      enableHighAccuracy: false,
      timeout: 5000,
      maximumAge: 10 * 60 * 1000,
    });
  }
};

const RRRORIGINKEY = "rrr-origin";
const MNIORIGIN = "mni";
const SPOTIFYORIGIN = "spotify";

// util
const getSessionStorageSessionOrigin = () =>
  window.sessionStorage.getItem(RRRORIGINKEY);

// util
const setSessionStorageSessionOrigin = (origin: string) => {
  window.sessionStorage.setItem(RRRORIGINKEY, origin);
};

// helper - do not export
const specialMNIActions = ({
  eventType,
  data,
  metadata,
}: {
  eventType: EventType;
  data?: EventData;
  metadata?: Metadata;
}) => {
  if (
    metadata?.referrer.toLowerCase().includes("mni") ||
    metadata?.qsps.toLowerCase().includes("mni")
  ) {
    setSessionStorageSessionOrigin(MNIORIGIN);
  }

  if (
    eventType == "view" &&
    metadata?.url.toLowerCase().includes("registration-complete") &&
    getSessionStorageSessionOrigin() == MNIORIGIN
  ) {
    // fire mni pixels
    fbq?.("trackCustom", "Registration Completed");
  }
};

// fire any riddler-owned analytics tags based on events
const specialInternalActions = ({
  eventType,
  data,
  metadata,
}: {
  eventType: EventType;
  data?: EventData;
  metadata?: Metadata;
}) => {
  if (
    metadata?.referrer.toLowerCase().includes("spotify") ||
    metadata?.qsps.toLowerCase().includes("spotify")
  ) {
    setSessionStorageSessionOrigin(SPOTIFYORIGIN);
  }

  if (
    eventType == "action" &&
    (data?.action == "click-join-team" || data?.action == "click-register-team")
  ) {
    if (data?.eventId)
      externalTrackClickRegisterOrJoinTeam({
        eventId: data.eventId.toString(),
      });
    else if (data?.rodEventId)
      externalTrackClickRegisterOrJoinTeam({
        eventId: data.rodEventId.toString(),
      });
  }

  if (eventType == "view" && metadata?.url.includes("/event/?eventId=")) {
    externalTrackEventView({ eventId: data?.eventId?.toString() || "-1" });
  }

  if (eventType == "view" && metadata?.url.includes("/past-event/?eventId=")) {
    externalTrackEventView({ eventId: data?.rodEventId?.toString() || "-1" });
  }

  if (eventType == "view" && metadata?.url.includes("/registration-complete")) {
    externalTrackRegistrationComplete({
      eventId: data?.eventId?.toString() || "-1",
    });
  }

  if (
    eventType == "view" &&
    metadata?.title.toLowerCase() === "riddler road rally"
  ) {
    externalTrackHomepageView();
  }
};

// this is called in logEvent, so we are able to hook into other actions based on event data

export const specialEventActions = ({
  eventType,
  data,
  metadata,
}: {
  eventType: EventType;
  data?: EventData;
  metadata?: Metadata;
}) => {
  specialInternalActions({ eventType, data, metadata });
  specialMNIActions({ eventType, data, metadata });
};

/* EXTERNAL EVENT TAGS */

const externalTrackClickRegisterOrJoinTeam = (props: { eventId: string }) => {
  fbq?.("trackCustom", "Register Button");
  gtag?.("event", "conversion", {
    allow_custom_scripts: true,
    send_to: "DC-15289717/tr/track0+standard",
  });
  gtag?.("event", "conversion", {
    send_to: "AW-16900819816/FPygCKuni6YaEOiW-Po-",
  });
  if (getSessionStorageSessionOrigin() === SPOTIFYORIGIN) {
    spdt?.("signup", {
      event_id: props.eventId,
    });
  }
};

const externalTrackEventView = (props: { eventId: string }) => {
  gtag?.("event", "conversion", {
    allow_custom_scripts: true,
    send_to: "DC-15289717/tr/track00+standard",
  });
  if (getSessionStorageSessionOrigin() === SPOTIFYORIGIN) {
    spdt?.("product", {
      event_id: props.eventId.toString(),
    });
  }
};

const externalTrackRegistrationComplete = (props: { eventId: string }) => {
  if (
    props.eventId !== "-1" &&
    getSessionStorageSessionOrigin() === SPOTIFYORIGIN
  ) {
    spdt?.("purchase", {
      event_id: props.eventId,
    });
  }
  gtag?.("event", "conversion", {
    allow_custom_scripts: true,
    session_id: "[SessionID]",
    send_to: "DC-15289717/co/conve0+per_session",
  });
  gtag?.("event", "conversion", {
    send_to: "AW-16900819816/psBhCK6ni6YaEOiW-Po-",
  });
};

const externalTrackHomepageView = () => {
  gtag?.("event", "conversion", {
    allow_custom_scripts: true,
    send_to: "DC-15289717/re/retar0+standard",
  });
};
